"use client"

// components/tracker/filter-dropdown.tsx
//
// A filter chip that opens a plain list with counts (FE-DRUG-PAGE-1 Phase
// 4a-ii). PZH-0 draws the four chips closed only (Q2J-0); the open state is
// an undrawn tracker pattern (plan decision: a plain dropdown list with
// counts, listed for a Paper check afterwards). Shared so the four facet
// chips and the sort control on the Drugs page are one control, not five
// copies. Deliberately small: no portal, no shared popover primitive.
//
// Keyboard: Escape closes, ArrowUp / ArrowDown move, Home / End jump, Enter
// or Space toggles the focused option. `aria-expanded` on the chip,
// `role="listbox"` on the list, `aria-selected` on each option.

import { useCallback, useEffect, useId, useRef, useState } from "react"

import styles from "@/components/tracker/filter-dropdown.module.css"

export type FilterOption = {
  value: string
  label: string
  /** Drugs carrying this value, shown at the right of the row. Omit to hide. */
  count?: number
}

export function FilterDropdown({
  label,
  options,
  selected,
  onSelect,
  multiple = true,
  plain = false,
  alignRight = false,
  emptyText = "Nothing to filter on yet",
  ariaLabel,
}: {
  /** The chip's text ("Therapy area"); a selection count follows it. */
  label: string
  options: FilterOption[]
  selected: string[]
  /** Called with the option's value; the parent decides toggle semantics. */
  onSelect: (value: string) => void
  /** False for a single-choice control (the sort): choosing closes the list. */
  multiple?: boolean
  /** The Q2S-0 recipe: plain text, no chip chrome. */
  plain?: boolean
  alignRight?: boolean
  emptyText?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const chipRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listId = useId()

  const close = useCallback((refocus: boolean) => {
    setOpen(false)
    if (refocus) chipRef.current?.focus()
  }, [])

  // Close on an outside pointerdown. `pointerdown` rather than `click`, so
  // choosing an item in another control does not need two taps.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onDown)
    return () => document.removeEventListener("pointerdown", onDown)
  }, [open])

  // Focus moves into the list on open so the arrow keys work at once.
  useEffect(() => {
    if (!open) return
    setFocusIdx(0)
    listRef.current?.focus()
  }, [open])

  const onListKey = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const n = options.length
    if (e.key === "Escape") {
      e.preventDefault()
      close(true)
      return
    }
    if (e.key === "Tab") {
      setOpen(false)
      return
    }
    if (n === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setFocusIdx((i) => (i + 1) % n)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusIdx((i) => (i - 1 + n) % n)
    } else if (e.key === "Home") {
      e.preventDefault()
      setFocusIdx(0)
    } else if (e.key === "End") {
      e.preventDefault()
      setFocusIdx(n - 1)
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      const opt = options[focusIdx]
      if (opt) {
        onSelect(opt.value)
        if (!multiple) close(true)
      }
    }
  }

  const onChipKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault()
      setOpen(true)
    } else if (e.key === "Escape" && open) {
      e.preventDefault()
      close(true)
    }
  }

  const count = selected.length
  const chipText = multiple
    ? count > 0
      ? `${label} · ${count}`
      : label
    : label

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={chipRef}
        type="button"
        className={`${styles.chip} ${plain ? styles.chipPlain : ""} ${
          count > 0 && multiple ? styles.chipOn : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onChipKey}
      >
        <span>{chipText}</span>
        {/* The literal chevron character the artboard strings carry. */}
        <span className={styles.chevron} aria-hidden>
          ⌄
        </span>
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          className={`${styles.list} ${alignRight ? styles.listRight : ""}`}
          role="listbox"
          aria-label={ariaLabel ?? label}
          aria-multiselectable={multiple || undefined}
          aria-activedescendant={
            options[focusIdx] ? `${listId}-${focusIdx}` : undefined
          }
          tabIndex={-1}
          onKeyDown={onListKey}
        >
          {options.length === 0 ? (
            <li className={styles.empty} role="presentation">
              {emptyText}
            </li>
          ) : (
            options.map((opt, i) => {
              const on = selected.includes(opt.value)
              return (
                <li
                  key={opt.value}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={on}
                  className={`${styles.item} ${on ? styles.itemOn : ""} ${
                    i === focusIdx ? styles.itemFocus : ""
                  }`}
                  onMouseEnter={() => setFocusIdx(i)}
                  onClick={() => {
                    onSelect(opt.value)
                    if (!multiple) close(true)
                  }}
                >
                  <span className={styles.itemTick} aria-hidden>
                    {on ? "✓" : ""}
                  </span>
                  <span className={styles.itemLabel} title={opt.label}>
                    {opt.label}
                  </span>
                  {opt.count !== undefined ? (
                    <span className={styles.itemCount}>
                      {opt.count.toLocaleString("en-GB")}
                    </span>
                  ) : null}
                </li>
              )
            })
          )}
        </ul>
      ) : null}
    </div>
  )
}
